import { useEffect, useState } from 'react';

interface Props {
  /** Anchor element ref to position the tooltip near (a ref to the roadmap button) */
  anchorId: string;
  onDismiss: (dontShowAgain: boolean) => void;
}

export function RoadmapHelperTooltip({ anchorId, onDismiss }: Props) {
  const [rect, setRect] = useState<DOMRect | null>(null);

  useEffect(() => {
    function update() {
      const el = document.getElementById(anchorId);
      if (!el) return;
      setRect(el.getBoundingClientRect());
    }
    update();
    window.addEventListener('resize', update);
    window.addEventListener('scroll', update, true);
    return () => {
      window.removeEventListener('resize', update);
      window.removeEventListener('scroll', update, true);
    };
  }, [anchorId]);

  if (!rect) return null;

  // Position tooltip just below the button, right-aligned to it
  const top = rect.bottom + 12;
  const left = Math.max(8, rect.right - 280);

  return (
    <>
      {/* Dim background that doesn't block clicks */}
      <div className="fixed inset-0 z-[85] pointer-events-none bg-black/30 helper-fade" />

      {/* Highlight halo around the button */}
      <div
        className="fixed z-[86] pointer-events-none rounded-xl border-2 border-primary helper-pulse"
        style={{
          top: rect.top - 4,
          left: rect.left - 4,
          width: rect.width + 8,
          height: rect.height + 8,
        }}
      />

      {/* Arrow */}
      <div
        className="fixed z-[87] pointer-events-none"
        style={{ top: rect.bottom + 2, left: rect.left + rect.width / 2 - 6 }}
      >
        <div className="w-3 h-3 rotate-45 bg-primary" />
      </div>

      {/* Tooltip card */}
      <div
        className="fixed z-[87] w-[280px] rounded-xl bg-primary text-on-primary shadow-2xl p-4 helper-bounce"
        style={{ top, left }}
      >
        <div className="font-semibold text-sm mb-1">Tu roadmap está acá</div>
        <p className="text-xs opacity-90 mb-3">
          Hacé click cuando quieras ver los próximos pasos de tu tier actual.
        </p>
        <div className="flex items-center justify-end gap-2">
          <button
            onClick={() => onDismiss(true)}
            className="text-xs underline opacity-80 hover:opacity-100"
          >
            No volver a mostrar
          </button>
          <button
            onClick={() => onDismiss(false)}
            className="text-xs px-3 py-1.5 rounded-md bg-on-primary/20 hover:bg-on-primary/30"
          >
            Entendido
          </button>
        </div>
      </div>

      <style>{`
        @keyframes helper-fade-in { from { opacity: 0; } to { opacity: 1; } }
        .helper-fade { animation: helper-fade-in 0.25s ease-out both; }
        @keyframes helper-pulse-anim {
          0%, 100% { box-shadow: 0 0 0 0 rgba(120, 120, 255, 0.5); }
          50% { box-shadow: 0 0 0 8px rgba(120, 120, 255, 0); }
        }
        .helper-pulse { animation: helper-pulse-anim 1.4s ease-in-out infinite; }
        @keyframes helper-bounce-in {
          0% { transform: scale(0.9) translateY(-8px); opacity: 0; }
          100% { transform: scale(1) translateY(0); opacity: 1; }
        }
        .helper-bounce { animation: helper-bounce-in 0.3s cubic-bezier(0.34, 1.56, 0.64, 1) both; }
      `}</style>
    </>
  );
}
